import React, { ReactNode } from "react";
import styled from "styled-components";
import {
  BacktestingRunType,
  BacktestingSort,
  BotType,
  LanguageNameSpaces,
  SortingOrder,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import Panel from "common/NewLook/Panel.tsx";
import SectionHeader from "common/NewLook/SectionHeader.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import PanelEmpty from "common/Empty/PanelEmpty.tsx";
import { getNewLookTokens, NEW_LOOK_RADIUS, getPanelShadow } from "common/NewLook/tokens.ts";
import BacktestingCard from "features/bots/BotForm/Backtesting/BacktestingCard";
import NewLookBacktestingTable from "features/bots/BotForm/Backtesting/NewLook/NewLookBacktestingTable";

interface NewLookBacktestingListProps {
  botType: BotType;
  data: BacktestingRunType[];
  isCardsView: boolean;
  /** Nothing loaded yet. */
  isLoading?: boolean;
  /** There are runs, only the search or filters hide them. */
  isFiltered?: boolean;
  /** The search field, in the list's header. */
  search?: ReactNode;
  openDeleteModal: (backtesting: BacktestingRunType) => void;
  onRowClick: (backtesting: BacktestingRunType) => void;
  sortBy?: BacktestingSort;
  setSortBy: (sortBy: BacktestingSort) => void;
  sortDirection: SortingOrder;
  setSortDirection: (sortDirection: SortingOrder) => void;
}

/**
 * New look (desktop): the backtests list. As a table it is one panel — the
 * title, count and search in its header and the rows edge to edge; as cards
 * the same header sits above the grid. Loading shows skeletons in the shape
 * of the content; an empty list says why inside the panel.
 */
const NewLookBacktestingList = ({
  botType,
  data,
  isCardsView,
  isLoading,
  isFiltered,
  search,
  openDeleteModal,
  onRowClick,
  ...sortProps
}: NewLookBacktestingListProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const title = t("backtesting.labels.backtests");
  const count = isLoading ? undefined : data.length;
  const searchSlot = search ? <SearchSlot>{search}</SearchSlot> : undefined;

  const empty = isFiltered ? (
    <PanelEmpty
      title={t("backtesting.empty_states.no_matching_backtests")}
      description={t("backtesting.empty_states.no_matching_description")}
    />
  ) : (
    <PanelEmpty
      title={t("backtesting.empty_states.no_backtesting_history")}
      description={t("backtesting.empty_states.no_backtests_description")}
    />
  );

  if (!isCardsView || (!isLoading && !data.length)) {
    return (
      <ListPanel
        title={title}
        count={count}
        actions={searchSlot}
        flush={true}
        data-testid="backtesting-list"
      >
        {!isLoading && !data.length ? (
          empty
        ) : (
          <NewLookBacktestingTable
            botType={botType}
            data={data}
            openDeleteModal={openDeleteModal}
            onRowClick={onRowClick}
            emptyState={empty}
            loading={isLoading}
            {...sortProps}
          />
        )}
      </ListPanel>
    );
  }

  return (
    <div data-testid="backtesting-list">
      <SectionHeader title={title} count={count} actions={searchSlot} />
      <Grid>
        {isLoading
          ? Array.from({ length: 6 }, (_, index) => (
              <CardSkeleton key={index} aria-busy={true}>
                <SkeletonRow>
                  <Skeleton width={32} height={32} radius={16} />
                  <SkeletonLines>
                    <Skeleton width="70%" height={12} />
                    <Skeleton width="45%" height={10} />
                  </SkeletonLines>
                </SkeletonRow>
                <SkeletonRow>
                  <SkeletonLines>
                    <Skeleton width={64} height={10} />
                    <Skeleton width={96} height={18} />
                  </SkeletonLines>
                  <SkeletonLines>
                    <Skeleton width={64} height={10} />
                    <Skeleton width={120} height={14} />
                  </SkeletonLines>
                </SkeletonRow>
                <Skeleton width="100%" height={12} />
              </CardSkeleton>
            ))
          : data.map((item) => (
              <BacktestingCard
                key={item.id}
                botType={botType}
                data={item}
                setBacktestingToDelete={openDeleteModal}
                isSeeAllPage={true}
              />
            ))}
      </Grid>
    </div>
  );
};

export default NewLookBacktestingList;

const ListPanel = styled(Panel)`
  overflow: hidden;
`;
const SearchSlot = styled.div`
  width: 240px;
  > * {
    width: 100% !important;
  }
  .ant-input-affix-wrapper {
    height: 32px;
  }
  @media (max-width: 900px) {
    width: 200px;
  }
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
  align-items: start;
`;
const CardSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 16px;
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  box-shadow: ${(p) => getPanelShadow(p.theme)};
`;
const SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  > * {
    flex: 1;
  }
  > span:first-child {
    flex: none;
  }
`;
const SkeletonLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
